import { useEffect, useMemo, useState } from 'react';

import {
  getStudentHonorsPortalState,
  isCurrentHonorsProgramMember
} from '../../utils/honorsSignups';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders() {
  return {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };
}

async function readJson(response, label) {
  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  return text ? JSON.parse(text) : [];
}

async function fetchAllCourses() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?is_honors=eq.true&is_live=eq.true&select=id,name,is_live,is_honors,course_title_id,semester_id,course_series_id,course_titles(name),semesters(name,start_date)`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading Honors courses'
  );
}

async function fetchCourseIdsWithAssignedReadings(courseIds) {
  const ids =
    (Array.isArray(courseIds)
      ? courseIds
      : [])
      .filter(Boolean)
      .map(String);

  if (!ids.length) {
    return new Set();
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?select=course_readings!inner(id),course_weeks!inner(course_id)&course_weeks.course_id=in.(${ids.join(',')})`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading courses with readings'
  );

  return new Set(
    (Array.isArray(rows) ? rows : [])
      .map(row =>
        row?.course_weeks?.course_id
      )
      .filter(Boolean)
      .map(String)
  );
}

async function fetchCourseReadings(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?select=id,display_order,course_reading_id,course_readings(id,course_id,title,author,drive_file_id,drive_file_name),course_weeks!inner(course_id)&course_weeks.course_id=eq.${courseId}&order=display_order.asc`,
    { headers: apiHeaders() }
  );

  const assignments = await readJson(response, 'Loading course readings');

  // Only readings that are currently assigned to at least one week count as
  // course readings for this page. A reading may be assigned to several weeks,
  // so collapse duplicate assignment rows to one reading entry.
  const byId = new Map();

  (Array.isArray(assignments) ? assignments : []).forEach(assignment => {
    const reading = assignment?.course_readings;
    if (reading?.id && !byId.has(reading.id)) {
      byId.set(reading.id, reading);
    }
  });

  return [...byId.values()].sort((a, b) => {
    const authorCompare = String(a.author || '').localeCompare(String(b.author || ''));
    if (authorCompare !== 0) return authorCompare;
    return String(a.title || '').localeCompare(String(b.title || ''));
  });
}

function unwrapCourse(value) {
  return value?.representative || value;
}

function getCourseName(value) {
  const course =
    unwrapCourse(value);

  return (
    course?.course_titles?.name ||
    course?.name ||
    'Untitled Course'
  );
}

function getSemesterName(value) {
  if (
    Array.isArray(value?.offerings) &&
    value.offerings.length
  ) {
    const names =
      value.offerings
        .map(offering =>
          offering
            ?.semesters
            ?.name
        )
        .filter(Boolean);

    if (names.length === 1) {
      return names[0];
    }

    if (names.length > 1) {
      return (
        names[0] +
        ' – ' +
        names[names.length - 1]
      );
    }
  }

  const course =
    unwrapCourse(value);

  return (
    course?.semesters?.name ||
    'Semester not specified'
  );
}

function groupCoursesBySeries(courses) {
  const bySeries =
    new Map();

  (Array.isArray(courses)
    ? courses
    : [])
    .forEach(course => {
      const key =
        course.course_series_id ||
        `course:${course.id}`;

      if (!bySeries.has(key)) {
        bySeries.set(key, []);
      }

      bySeries.get(key).push(course);
    });

  return [...bySeries.entries()]
    .map(([key, offerings]) => {
      const ordered =
        [...offerings].sort(
          (a, b) =>
            String(
              a?.semesters
                ?.start_date ||
              ''
            ).localeCompare(
              String(
                b?.semesters
                  ?.start_date ||
                ''
              )
            )
        );

      const live =
        [...ordered]
          .reverse()
          .find(course =>
            course.is_live
          );

      const representative =
        live ||
        ordered[
          ordered.length - 1
        ] ||
        null;

      return {
        id: key,
        key,
        offerings: ordered,
        representative
      };
    })
    .filter(
      group =>
        group.representative
    )
    .sort((a, b) => {
      const aDate =
        a.representative
          ?.semesters
          ?.start_date ||
        '';

      const bDate =
        b.representative
          ?.semesters
          ?.start_date ||
        '';

      if (aDate !== bDate) {
        return bDate.localeCompare(
          aDate
        );
      }

      return getCourseName(a)
        .localeCompare(
          getCourseName(b)
        );
    });
}

export default function StudentAllCourseReadings({ session, onBack }) {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [readings, setReadings] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingReadings, setLoadingReadings] = useState(false);
  const [previewReading, setPreviewReading] = useState(null);
  const [accessAllowed, setAccessAllowed] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');

      try {
        const portalState =
          await getStudentHonorsPortalState(
            session
          );

        const isHonors =
          isCurrentHonorsProgramMember(
            portalState
          );

        if (cancelled) return;

        setAccessAllowed(
          isHonors
        );

        if (isHonors) {
          const allCourses =
            await fetchAllCourses();

          if (cancelled) return;
          const courseRows =
            Array.isArray(allCourses)
              ? allCourses
              : [];

          const courseIdsWithReadings =
            await fetchCourseIdsWithAssignedReadings(
              courseRows.map(
                course => course.id
              )
            );

          if (cancelled) return;

          setCourses(
            groupCoursesBySeries(
              courseRows
            ).filter(
              courseSeries =>
                (
                  Array.isArray(
                    courseSeries.offerings
                  )
                    ? courseSeries.offerings
                    : []
                ).some(
                  offering =>
                    courseIdsWithReadings.has(
                      String(offering.id)
                    )
                )
            )
          );
        }
      } catch (err) {
        console.error('Failed to load all-course readings:', err);

        if (!cancelled) {
          setError(err?.message || 'Failed to load course readings.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [session]);

  const filteredCourses = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return courses;

    return courses.filter(courseSeries =>
      [
        getCourseName(
          courseSeries
        ),
        getSemesterName(
          courseSeries
        ),
        ...(
          Array.isArray(
            courseSeries.offerings
          )
            ? courseSeries.offerings
                .map(offering =>
                  offering
                    ?.semesters
                    ?.name
                )
            : []
        )
      ]
        .filter(Boolean)
        .some(value =>
          String(value)
            .toLowerCase()
            .includes(term)
        )
    );
  }, [courses, search]);

  async function handleSelectCourse(
    courseSeries
  ) {
    setSelectedCourse(
      courseSeries
    );
    setReadings([]);
    setPreviewReading(null);
    setLoadingReadings(true);
    setError('');

    try {
      const offerings =
        Array.isArray(
          courseSeries.offerings
        )
          ? courseSeries.offerings
          : [];

      const bySemester =
        await Promise.all(
          offerings.map(
            async (
              offering,
              index
            ) => {
              const rows =
                await fetchCourseReadings(
                  offering.id
                );

              return (
                Array.isArray(rows)
                  ? rows
                  : []
              ).map(reading => ({
                ...reading,
                series_course_id:
                  offering.id,
                series_semester_index:
                  index,
                series_semester_name:
                  offering
                    ?.semesters
                    ?.name ||
                  `Semester ${index + 1}`
              }));
            }
          )
        );

      setReadings(
        bySemester.flat()
      );
    } catch (err) {
      console.error(
        'Failed to load course-series readings:',
        err
      );

      setError(
        err?.message ||
        'Failed to load readings for this course.'
      );
    } finally {
      setLoadingReadings(false);
    }
  }

  function handleBackToCourses() {
    setSelectedCourse(null);
    setReadings([]);
    setPreviewReading(null);
    setError('');
  }


  const selectedReadingGroups =
    useMemo(() => {
      if (!selectedCourse) {
        return [];
      }

      const offerings =
        Array.isArray(
          selectedCourse.offerings
        )
          ? selectedCourse.offerings
          : [];

      return offerings.map(
        (offering, index) => ({
          course: offering,
          index,
          readings:
            readings.filter(
              reading =>
                Number(
                  reading
                    .series_semester_index
                ) === index
            )
        })
      );
    }, [
      selectedCourse,
      readings
    ]);

  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.content}>
          <div style={styles.muted}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!accessAllowed) {
    return (
      <div style={styles.wrap}>
        <header style={styles.header}>
          <button style={styles.backBtn} onClick={onBack}>
            ← Main Menu
          </button>
          <div style={styles.headerTitle}>Course Readings</div>
          <div />
        </header>

        <div style={styles.content}>
          <div style={styles.error}>
            Course Readings (All Courses) is available to Honors students only.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <header style={styles.header}>
        <button
          style={styles.backBtn}
          onClick={selectedCourse ? handleBackToCourses : onBack}
        >
          ← {selectedCourse ? 'All Courses' : 'Main Menu'}
        </button>

        <div style={styles.headerTitle}>
          Course Readings (All Courses)
        </div>

        <div />
      </header>

      <div style={styles.content}>
        {previewReading && (
          <div
            style={styles.previewOverlay}
            onClick={() => setPreviewReading(null)}
          >
            <div
              style={styles.previewPanel}
              onClick={event => event.stopPropagation()}
            >
              <div style={styles.previewHeader}>
                <div>
                  <div style={styles.previewTitle}>
                    {previewReading.title || 'Untitled Reading'}
                  </div>
                  {previewReading.author && (
                    <div style={styles.previewAuthor}>
                      by {previewReading.author}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  style={styles.previewClose}
                  onClick={() => setPreviewReading(null)}
                >
                  ×
                </button>
              </div>

              {previewReading.drive_file_id ? (
                <iframe
                  src={`https://drive.google.com/file/d/${previewReading.drive_file_id}/preview`}
                  style={styles.previewFrame}
                  title={previewReading.title || 'Course reading'}
                />
              ) : (
                <div style={styles.previewNoFile}>No file available.</div>
              )}

              {previewReading.drive_file_id && (
                <a
                  href={`https://drive.google.com/uc?export=download&id=${previewReading.drive_file_id}`}
                  target="_blank"
                  rel="noreferrer"
                  style={styles.downloadLink}
                >
                  Download
                </a>
              )}
            </div>
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        {!selectedCourse ? (
          <>
            <div style={styles.introBox}>
              <h1 style={styles.heading}>Course Readings</h1>
              <div style={styles.introText}>
                Browse the reading libraries for all courses in the system.
              </div>

              <input
                type="text"
                style={styles.searchInput}
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search courses or semesters..."
              />
            </div>

            <div style={styles.resultCount}>
              {filteredCourses.length} course
              {filteredCourses.length !== 1 ? 's' : ''}
            </div>

            <div style={styles.courseList}>
              {filteredCourses.map(courseSeries => (
                <button
                  key={courseSeries.id}
                  type="button"
                  style={styles.courseCard}
                  onClick={() =>
                    handleSelectCourse(
                      courseSeries
                    )
                  }
                >
                  <div style={styles.courseMain}>
                    <div style={styles.courseName}>
                      {getCourseName(
                        courseSeries
                      )}
                    </div>

                    <div style={styles.courseMeta}>
                      {getSemesterName(
                        courseSeries
                      )}
                    </div>
                  </div>

                  {courseSeries
                    .offerings
                    ?.some(
                      offering =>
                        offering.is_live
                    ) && (
                    <span style={styles.livePill}>
                      Current
                    </span>
                  )}

                  <div style={styles.courseArrow}>
                    →
                  </div>
                </button>
              ))}

              {filteredCourses.length === 0 && (
                <div style={styles.emptyBox}>
                  No courses match your search.
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div style={styles.selectedHeader}>
              <h1 style={styles.heading}>
                {getCourseName(selectedCourse)}
              </h1>
              <div style={styles.selectedSemester}>
                {getSemesterName(selectedCourse)}
              </div>
            </div>

            {loadingReadings ? (
              <div style={styles.muted}>
                Loading readings...
              </div>
            ) : readings.length === 0 ? (
              <div style={styles.emptyBox}>
                No course readings are currently available for this course.
              </div>
            ) : (
              <div style={styles.semesterReadingGroups}>
                {selectedReadingGroups.map(
                  group => (
                    <section
                      key={
                        group.course.id
                      }
                      style={
                        styles.semesterReadingGroup
                      }
                    >
                      <div
                        style={
                          styles.semesterReadingHeading
                        }
                      >
                        <span>
                          Semester {
                            group.index + 1
                          }
                        </span>

                        <strong>
                          {group.course
                            ?.semesters
                            ?.name ||
                            'Semester'}
                        </strong>
                      </div>

                      {group.readings.length === 0 ? (
                        <div style={styles.emptyBox}>
                          No readings are assigned in this semester.
                        </div>
                      ) : (
                        <div style={styles.readingList}>
                          {group.readings.map(
                            reading => (
                              <div
                                key={
                                  `${group.course.id}:${reading.id}`
                                }
                                style={
                                  styles.readingCard
                                }
                              >
                                <div style={styles.readingInfo}>
                                  <div style={styles.readingTitle}>
                                    {reading.title ||
                                      'Untitled Reading'}
                                  </div>

                                  {reading.author && (
                                    <div style={styles.readingAuthor}>
                                      {reading.author}
                                    </div>
                                  )}
                                </div>

                                <div style={styles.readingActions}>
                                  {reading.drive_file_id ? (
                                    <>
                                      <button
                                        type="button"
                                        style={styles.secondaryBtn}
                                        onClick={() =>
                                          setPreviewReading(
                                            reading
                                          )
                                        }
                                      >
                                        Preview
                                      </button>

                                      <a
                                        href={`https://drive.google.com/uc?export=download&id=${reading.drive_file_id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={styles.downloadBtn}
                                      >
                                        Download
                                      </a>
                                    </>
                                  ) : (
                                    <span style={styles.noFile}>
                                      No file
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </section>
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: { minHeight: '100vh', background: '#f7f8fa' },

  header: {
    background: '#102a43',
    borderBottom: '1px solid #163752',
    padding: '14px 20px',
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 20
  },

  backBtn: {
    justifySelf: 'start',
    border: '1px solid rgba(255,255,255,0.28)',
    background: 'transparent',
    borderRadius: '5px',
    padding: '7px 11px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#d9e2ec',
    cursor: 'pointer'
  },

  headerTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: 'white',
    textAlign: 'center'
  },

  content: {
    padding: '30px 28px',
    maxWidth: '980px',
    margin: '0 auto'
  },

  introBox: {
    background: 'white',
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    padding: '20px',
    marginBottom: '14px',
    boxShadow: 'none'
  },

  heading: {
    margin: 0,
    fontSize: '27px',
    color: '#243b53',
    fontWeight: '700'
  },

  introText: {
    marginTop: '8px',
    color: '#627d98',
    fontSize: '14px',
    lineHeight: '1.5'
  },

  searchInput: {
    width: '100%',
    boxSizing: 'border-box',
    marginTop: '16px',
    border: '1px solid #bcccdc',
    borderRadius: '5px',
    padding: '10px 12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    background: 'white'
  },

  resultCount: {
    fontSize: '12px',
    color: '#829ab1',
    margin: '0 2px 8px'
  },

  courseList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },

  courseCard: {
    width: '100%',
    background: 'white',
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    textAlign: 'left',
    cursor: 'pointer'
  },

  courseMain: { flex: 1, minWidth: 0 },

  courseName: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#243b53'
  },

  courseMeta: {
    marginTop: '3px',
    fontSize: '12px',
    color: '#627d98'
  },

  livePill: {
    background: '#eef5fa',
    color: '#2f6f9f',
    borderRadius: '999px',
    padding: '3px 8px',
    fontSize: '10px',
    fontWeight: '800'
  },

  courseArrow: {
    color: '#2f6f9f',
    fontSize: '18px',
    fontWeight: '700'
  },

  selectedHeader: {
    background: 'white',
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    padding: '18px 20px',
    marginBottom: '14px'
  },

  selectedSemester: {
    marginTop: '4px',
    color: '#627d98',
    fontSize: '13px'
  },

  semesterReadingGroups: {
    display: 'flex',
    flexDirection: 'column',
    gap: '26px'
  },

  semesterReadingGroup: {
    minWidth: 0
  },

  semesterReadingHeading: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    paddingBottom: '8px',
    marginBottom: '10px',
    borderBottom: '1px solid #d9e2ec'
  },

  readingList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },

  readingCard: {
    background: 'white',
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px'
  },

  readingInfo: { flex: 1, minWidth: 0 },

  readingTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#243b53'
  },

  readingAuthor: {
    marginTop: '3px',
    fontSize: '12px',
    color: '#627d98'
  },

  readingActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },

  secondaryBtn: {
    border: '1px solid #bcccdc',
    background: 'white',
    color: '#2f6f9f',
    borderRadius: '4px',
    padding: '7px 10px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  downloadBtn: {
    display: 'inline-block',
    background: '#2f6f9f',
    color: 'white',
    borderRadius: '4px',
    padding: '8px 11px',
    fontSize: '12px',
    fontWeight: '700',
    textDecoration: 'none'
  },

  noFile: {
    color: '#9fb3c8',
    fontSize: '12px'
  },

  emptyBox: {
    background: 'white',
    border: '1px solid #d9e2ec',
    borderRadius: '6px',
    padding: '18px',
    color: '#627d98',
    fontSize: '14px'
  },

  muted: {
    color: '#627d98',
    fontSize: '14px'
  },

  error: {
    background: '#fff1f0',
    color: '#a33',
    border: '1px solid #f0c8c5',
    borderRadius: '5px',
    padding: '10px 12px',
    marginBottom: '14px',
    fontSize: '13px'
  },

  previewOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 300,
    padding: '20px'
  },

  previewPanel: {
    background: 'white',
    borderRadius: '6px',
    width: '100%',
    maxWidth: '800px',
    height: '88vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },

  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px 20px',
    borderBottom: '1px solid #d9e2ec'
  },

  previewTitle: {
    fontWeight: '700',
    fontSize: '16px',
    color: '#243b53'
  },

  previewAuthor: {
    fontSize: '13px',
    color: '#627d98',
    marginTop: '2px'
  },

  previewClose: {
    border: 'none',
    background: 'none',
    fontSize: '26px',
    cursor: 'pointer',
    color: '#627d98',
    lineHeight: 1
  },

  previewFrame: {
    flex: 1,
    border: 'none',
    minHeight: '450px'
  },

  previewNoFile: {
    padding: '20px',
    color: '#627d98',
    fontSize: '14px'
  },

  downloadLink: {
    display: 'block',
    padding: '12px 20px',
    color: '#2f6f9f',
    fontSize: '14px',
    fontWeight: '600',
    textDecoration: 'underline',
    borderTop: '1px solid #d9e2ec'
  }
};